/**
 * StreamingTimeline - Displays the live streaming state during agent execution.
 *
 * Renders timeline items in chronological order as they arrive from the WebSocket.
 * Each item (thinking, tool, content) appears in the exact order it was received.
 * Uses React.memo on children to avoid re-rendering unchanged items on each delta.
 */

import React, { useState, useMemo } from 'react';
import { AlertTriangle, WifiOff, RotateCcw } from 'lucide-react';
import { ThinkingGroup } from './ThinkingGroup';
import { ToolGroup } from './ToolGroup';
import { CompactionSeparator } from './CompactionSeparator';
import { TypingIndicator } from './TypingIndicator';
import { StreamingContent } from './StreamingContent';
import { TextShimmer } from './TextShimmer';
import { MessageActionButtons } from '../shared/MessageActionButtons';
import { SaveToResearchDialog } from '../shared/SaveToResearchDialog';
import { JurisprudenceSearchForm } from '../jurisprudence/JurisprudenceSearchForm';
import { JurisprudenceSearchProgress } from '../jurisprudence/JurisprudenceSearchProgress';
import { JurisprudenceSearchResults } from '../jurisprudence/JurisprudenceSearchResults';
import { ResetAnalysisCard } from '../casewiki/ResetAnalysisCard';
import { DeletePiecesConfirmationCard } from '../delete-pieces/DeletePiecesConfirmationCard';
import { RedactionPlanForm } from '../redaction/RedactionPlanForm';
import { RedactionPlanProgress } from '../redaction/RedactionPlanProgress';
import { RedactionPlanResults } from '../redaction/RedactionPlanResults';
import { useVisibleCanvasConversationId } from '@/features/redaction/state/canvasVisibilite';
import { toast } from 'sonner';
import { preparerEtOuvrirLeCanvasPourLAvocat } from '@/features/redaction/state/canvasHumanOpening';
import { REFUS_OUVERTURE_FIL_NON_PROUVE } from '@/features/redaction/state/canvasConversationGate';
import { masquerLeCanvas } from '@/features/redaction/state/canvasVisibilite';
import { useConversationAffichee } from '../../state/conversationAffichee';
import { AgentUiActionsCard } from '../ui-actions/AgentUiActionsCard';
import { SkillChangeCard } from '@/features/agent-skills';
import { groupTimelineItems } from '../../utils/groupTimelineItems';
import type { AgentUiActionId, ResolvedAgentUiAction } from '@hector/shared-types';
import type { TimelineItem } from '../../types/session.types';
import type { ExchangePhase } from '../../types/displayExchange';
import type { LegalQuestion, Jurisdiction, FondementJuridique } from '../jurisprudence/types';
import type { ChatMessage } from '@/types/chat';
import type { MessageFeedbackState } from '@hector/shared-types';
import type {
  RedactionPlanDraftSnapshot,
  RedactionPlanResponsePayload,
  SkillChangeResponse,
} from '@hector/shared-types';

type WebRedactionSources = NonNullable<RedactionPlanResponsePayload['sources']>;

const NOOP_REDACTION_SUBMIT = () => {};
const NOOP_REDACTION_CANCEL = () => {};
const NOOP_REDACTION_INTERACTION = () => false;

interface StreamingTimelineProps {
  timeline: TimelineItem[];
  /**
   * Cet hôte affiche-t-il le canvas ? Faux = la carte de plan ne propose pas de
   * l'ouvrir (#860). Le composeur ne pilote plus cette capability ; elle reste
   * réservée au point d'entrée associé à la carte de plan.
   */
  canOpenCanvas?: boolean;
  caseId?: string;
  /** Controls streaming/waiting/actions behavior derived from the exchange lifecycle. */
  phase: ExchangePhase;
  onJurisprudenceResponse?: (jobId: string, questions: LegalQuestion[], jurisdictions: Jurisdiction[], fondements: FondementJuridique[]) => void;
  onJurisprudenceCancelled?: (jobId: string) => void;
  /** Émet la décision explicite pour un reset destructif de l'Analyse avancée. */
  onCaseWikiResetResponse?: (payload: {
    caseId: string;
    accepted: boolean;
    requestId: string;
    caseWikiContextUserInputs?: string;
  }) => boolean;
  /** Émet le consentement/refus d'une suppression de pièces corrélée par nonce. */
  onDeletePiecesConfirmationResponse?: (payload: {
    caseId: string;
    accepted: boolean;
    requestId: string;
  }) => boolean;
  /** Soumission manuelle de la carte de validation du plan (clic Lancer). */
  onRedactionPlanSubmit?: (
    requestId: string,
    additionalInstructions: string | undefined,
    skipJurisprudence?: boolean,
    overrides?: { actId?: string; juridictions?: string[]; sources?: WebRedactionSources },
  ) => void;
  /** Annulation de la carte de validation du plan (clic Annuler). */
  onRedactionPlanCancel?: (requestId: string) => void;
  /** Réarme l'échéance après une modification effective du brouillon. */
  onRedactionPlanDraftUpdate?: (requestId: string, snapshot: RedactionPlanDraftSnapshot) => boolean;
  onSkillChangeResponse?: (payload: Omit<SkillChangeResponse, 'type'>) => boolean;
  /**
   * Route une action proposée par l'agent (`agent_ui_actions`). Contrairement aux
   * callbacks de consentement ci-dessus, aucun agent n'attend sa réponse ; la
   * Promise couvre seulement l'effet UI local et son état pending. Absent = boutons non rendus.
   */
  onUiAction?: (action: ResolvedAgentUiAction) => Promise<void>;
  /** Restreint les actions rendues à celles que l'hôte sait honorer (cf. V3ChatInterface). */
  uiActionCapabilities: readonly AgentUiActionId[];
  /**
   * Real `casediscovery_messages_v2.id` of this exchange's last assistant turn,
   * threaded down to MessageActionButtons to anchor the feedback thumbs. Undefined
   * on a still-live exchange (thumbs hidden until the terminal local snapshot).
   */
  feedbackMessageId?: string;
  /** Vote already cast for that message (re-lights the thumb on conversation open). */
  feedbackInitial?: MessageFeedbackState;
  /**
   * Relance le DERNIER prompt utilisateur après une erreur de génération
   * (item `agent_error`). Affiche un bouton « Réessayer » discret sous le message
   * d'erreur. Fourni uniquement sur l'échange live/échoué — `undefined` en historique
   * (où réessayer n'a pas de sens et casserait le `React.memo`).
   */
  onRetry?: () => void;
  /**
   * Vrai pour le DERNIER exchange de la conversation. Seul ce dernier porte la plume à
   * l'arrêt sous le message (une seule zone plume, collée au dernier message). Les exchanges
   * précédents n'affichent jamais de plume statique.
   */
  isLast?: boolean;
  /**
   * `Date.now()` origin of the live agent turn — origin of the feather timer.
   * Threaded from `session.turnStartedAt` (V3ChatInterface) rather than captured
   * locally, so it survives conversation switches / remounts: a turn that started
   * in the background shows its REAL elapsed time on return, not the time since
   * the conversation was opened. null when the turn is not active. (2026-06-22)
   */
  turnStartedAt?: number | null;
}


export const StreamingTimeline: React.FC<StreamingTimelineProps> = React.memo(({
  timeline,
  canOpenCanvas = false,
  caseId,
  phase,
  onJurisprudenceResponse,
  onJurisprudenceCancelled,
  onCaseWikiResetResponse,
  onDeletePiecesConfirmationResponse,
  onRedactionPlanSubmit,
  onRedactionPlanCancel,
  onRedactionPlanDraftUpdate,
  onUiAction,
  uiActionCapabilities,
  onSkillChangeResponse,
  feedbackMessageId,
  feedbackInitial,
  onRetry,
  isLast = false,
  turnStartedAt = null,
}) => {
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  // Le canvas est-il visible SUR CET ÉCRAN ? — pilote le libellé Afficher/Masquer
  // du bouton de la carte `RedactionPlanResults`. Depuis le 2026-08-13 ce n'est
  // plus un booléen global : le canvas ne s'affiche que chez le porteur du
  // document, donc la question n'a de sens que rapportée à la conversation
  // regardée. Elle est lue au registre plutôt que passée en accessoire — la
  // timeline est rendue par exchange, et threader la conversation jusqu'ici
  // traverserait quatre composants pour une valeur que le registre publie déjà.
  const conversationAffichee = useConversationAffichee();
  const conversationVisibleDuCanvas = useVisibleCanvasConversationId(conversationAffichee);
  const redactionCanvasOpen = conversationVisibleDuCanvas !== null;

  // Derive all behavior flags from the single `phase` prop.
  const isStreaming = phase === 'live_pending' || phase === 'live_streaming';
  const isWaitingForAgent = phase === 'live_pending';
  const hideActions = phase !== 'history' && phase !== 'live_done';
  // A form can outlive the editable composer for one render after an access
  // transition. Keep the proposed search visible, but never substitute a no-op
  // callback that would make a read-only form look actionable.
  const isJurisprudenceFormReadOnly = !onJurisprudenceResponse;

  /** Concatenate all content items for copy/save */
  const fullContent = useMemo(
    () => timeline
      .filter((i): i is Extract<TimelineItem, { type: 'content' }> => i.type === 'content')
      .map(i => i.content)
      .join('\n\n'),
    [timeline]
  );

  // Regroupement render-level des tool calls consécutifs en ToolGroup —
  // la timeline plate reste la source de vérité (reducer/persistance intacts).
  const displayItems = useMemo(() => groupTimelineItems(timeline), [timeline]);

  // Carte de boutons de navigation de l'agent (`propose_ui_actions`). On la sort
  // du flux pour la rendre TOUJOURS en fin de tour, sous le texte final — quel que
  // soit le moment où l'agent a appelé l'outil. WHY : ce sont des « étapes
  // suivantes », elles se lisent APRÈS la réponse ; et l'agent les annonce
  // « ci-dessous » dans sa prose. Rendue inline, la carte apparaissait au-dessus
  // du paragraphe de conclusion (l'agent appelle souvent l'outil avant d'écrire sa
  // dernière phrase) → contradiction avec « ci-dessous » (constaté 2026-07-17). Le
  // reducer ne garde qu'une carte par tour (remplacement), donc `find` suffit.
  const uiActionsItem = useMemo(
    () =>
      timeline.find(
        (i): i is Extract<TimelineItem, { type: 'ui_actions' }> => i.type === 'ui_actions',
      ),
    [timeline],
  );

  /**
   * Actions effectivement rendues. Filtrées sur ce que l'hôte sait honorer :
   * l'agent choisit dans un catalogue commun à toutes les surfaces, mais une
   * action que l'hôte courant ne gère pas donnerait un bouton inerte. Sans
   * La même liste autoritative est annoncée au backend et appliquée ici comme
   * dernière défense contre une frame incohérente.
   */
  const renderedUiActions = useMemo(() => {
    if (!uiActionsItem) return [];
    return uiActionsItem.actions.filter((a) => uiActionCapabilities.includes(a.id));
  }, [uiActionsItem, uiActionCapabilities]);

  // Origine du timer de la plume = `turnStartedAt` (prop), portée par la session
  // (cf. SessionData.turnStartedAt). PAS un Date.now() local : sinon le remount au
  // switch de conversation (clé = id d'exchange) réinitialiserait le compteur à
  // « depuis l'ouverture de la conv » au lieu de « depuis le dernier message ».

  if (!isStreaming && !isWaitingForAgent && timeline.length === 0) return null;

  // La plume reste affichée pendant TOUT le tour de l'agent (planification, réflexion,
  // tools, subagents, écriture) — c'est l'indicateur global « l'agent travaille » + timer.
  // On ne la cache plus quand un bloc thinking/tool est actif (elle disparaissait sinon).
  const showTypingIndicator = isStreaming;
  const showActionButtons = !isStreaming && !hideActions && fullContent.length > 0;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6">
      {/* group : survoler le message révèle la barre d'actions (copier / feedback…). */}
      <div className="group py-2">
        {/* Timeline content — assistant responses render as plain text, no avatar / name label */}
        <div className="min-w-0">
          <div className="space-y-1">
            {/* "Planification" shimmer while waiting for backend to start streaming */}
            {isWaitingForAgent && timeline.length === 0 && (
              <div className="py-1">
                <TextShimmer className="text-[13px] font-normal text-grey-600">Planification</TextShimmer>
              </div>
            )}

            {displayItems.map((displayItem, displayIndex) => {
              if (displayItem.kind === 'thinking_group') {
                // Réflexions autonomes (pas liées à un tool), hoistées en tête.
                return <ThinkingGroup key={displayItem.id} thinkings={displayItem.thinkings} />;
              }

              if (displayItem.kind === 'tool_group') {
                // La salve est close dès qu'un item la suit dans la timeline,
                // ou quand l'échange est terminé (historique / live_done) —
                // pilote le nœud « Terminé » et le shimmer de la phrase.
                const isClosed =
                  displayIndex < displayItems.length - 1 ||
                  phase === 'history' ||
                  phase === 'live_done';
                return (
                  <ToolGroup
                    key={displayItem.id}
                    members={displayItem.members}
                    isClosed={isClosed}
                  />
                );
              }

              const item = displayItem.item;
              switch (item.type) {
                case 'tool':
                  // Seul context_compaction traverse le regroupement
                  // (groupTimelineItems) — tous les autres tools sont rendus
                  // via ToolGroup ci-dessus.
                  return <CompactionSeparator key={item.id} status={item.event.status} />;

                case 'content':
                  return item.content.length > 0 ? (
                    <StreamingContent
                      key={item.id}
                      content={item.content}
                      isStreaming={isStreaming}
                      className="prose prose-sm max-w-none text-[13px] text-grey-800 leading-relaxed"
                    />
                  ) : null;

                case 'jurisprudence_form':
                  return (
                    <JurisprudenceSearchForm
                      key={item.id}
                      jobId={item.jobId}
                      initialQuestions={item.questions}
                      initialJurisdictions={item.jurisdictions}
                      initialFondements={item.fondements}
                      autoApprove={item.autoApprove && !isJurisprudenceFormReadOnly}
                      isReadOnly={isJurisprudenceFormReadOnly}
                      onSubmit={onJurisprudenceResponse}
                      onCancel={onJurisprudenceCancelled}
                    />
                  );

                case 'jurisprudence_progress':
                  return (
                    <JurisprudenceSearchProgress
                      key={item.id}
                      jobId={item.jobId}
                      questions={item.questions}
                    />
                  );

                case 'jurisprudence_results':
                  return (
                    <JurisprudenceSearchResults
                      key={item.id}
                      jobId={item.jobId}
                      caseId={caseId}
                      responseText={item.responseText}
                      sources={item.sources}
                      jurisprudences={item.jurisprudences}
                      fondements={item.fondements}
                    />
                  );

                case 'case_wiki_reset':
                  return (
                    <ResetAnalysisCard
                      key={item.id}
                      caseId={item.caseId}
                      caseTitle={item.caseTitle}
                      clientName={item.clientName}
                      requestId={item.requestId}
                      caseWikiContextUserInputs={item.caseWikiContextUserInputs}
                      result={item.result}
                      onRespond={onCaseWikiResetResponse}
                    />
                  );

                case 'delete_pieces_confirmation':
                  return (
                    <DeletePiecesConfirmationCard
                      key={item.id}
                      caseId={item.caseId}
                      caseTitle={item.caseTitle}
                      requestId={item.requestId}
                      pieces={item.pieces}
                      result={item.result}
                      onRespond={onDeletePiecesConfirmationResponse}
                    />
                  );

                case 'redaction_plan_form':
                  return (
                    <RedactionPlanForm
                      key={item.id}
                      requestId={item.requestId}
                      caseId={caseId}
                      autoLaunchAt={item.autoLaunchAt}
                      autoLaunchDraftUpdatePending={item.autoLaunchDraftUpdatePending}
                      autoLaunchDraftSnapshot={item.autoLaunchDraftSnapshot}
                      proposedPayload={item.proposedPayload}
                      onSubmit={onRedactionPlanSubmit ?? NOOP_REDACTION_SUBMIT}
                      onCancel={onRedactionPlanCancel ?? NOOP_REDACTION_CANCEL}
                      onInteract={onRedactionPlanDraftUpdate ?? NOOP_REDACTION_INTERACTION}
                    />
                  );

                case 'redaction_plan_progress':
                  return (
                    <RedactionPlanProgress
                      key={item.id}
                      requestId={item.requestId}
                      jobId={item.jobId}
                      kind={item.kind}
                      phase={item.phase}
                      argumentTitles={item.argumentTitles}
                      completedQueries={item.completedQueries}
                      totalQueries={item.totalQueries}
                      searchQueries={item.searchQueries}
                      legalVerification={item.legalVerification}
                      skipJurisprudence={item.skipJurisprudence}
                      // Lu sur le payload plutôt que porté par un champ dérivé : c'est le
                      // serveur qui décide du déroulé, l'écran ne fait que le refléter.
                      fullDocument={item.proposedPayload?.full_document === true}
                      errorMessage={item.errorMessage}
                      errorPhase={item.errorPhase}
                      onRetry={onRetry}
                      proposedPayload={item.proposedPayload}
                    />
                  );

                case 'redaction_plan_results':
                  return (
                    <RedactionPlanResults
                      key={item.id}
                      requestId={item.requestId}
                      jobId={item.jobId}
                      kind={item.kind}
                      verdict={item.verdict}
                      proposedPayload={item.proposedPayload}
                      extras={item.extras}
                      canvasOpen={redactionCanvasOpen}
                      onToggleCanvas={canOpenCanvas
                        ? () => {
                            if (conversationVisibleDuCanvas) {
                              masquerLeCanvas(conversationVisibleDuCanvas);
                              return;
                            }
                            // ⚠️ **Cette carte peut exister dans un fil que CE
                            // navigateur n'a pas encore prouvé** (#1331). Après
                            // un rechargement de page, la confirmation temps
                            // réel est perdue avec l'onglet et le détail REST
                            // peut être encore en vol pendant que la frise
                            // réhydratée s'affiche déjà. Le refus est donc un
                            // cas nominal, pas seulement le changement de
                            // compte — et un `console.warn` ne s'adresse à
                            // personne : l'avocat cliquerait sur un bouton muet.
                            void preparerEtOuvrirLeCanvasPourLAvocat(conversationAffichee)
                              .then((ouverture) => {
                                if (ouverture === null) toast.error(REFUS_OUVERTURE_FIL_NON_PROUVE);
                              });
                          }
                        : undefined}
                      progressSnapshot={item.progressSnapshot}
                    />
                  );

                case 'ui_actions':
                  // Rendue en fin de tour (cf. `uiActionsItem` plus bas), pas
                  // inline : sinon la carte se placerait là où l'outil a été
                  // appelé, souvent AVANT le texte de conclusion.
                  return null;
                case 'skill_change':
                  return (
                    <SkillChangeCard
                      key={item.id}
                      request={item.request}
                      result={item.result}
                      onRespond={onSkillChangeResponse}
                    />
                  );

                case 'disconnected':
                  return (
                    <div
                      key={item.id}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-600 text-[13px]"
                    >
                      <WifiOff className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>Connexion perdue, tentative de reconnexion…</span>
                    </div>
                  );

                case 'agent_error':
                  return (
                    <div
                      key={item.id}
                      className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-700"
                    >
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <div className="min-w-0">
                        <span>{item.message}</span>
                        {onRetry && (
                          <button
                            type="button"
                            onClick={onRetry}
                            className="mt-1.5 flex items-center gap-1 text-[13px] font-normal text-amber-800 underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none"
                          >
                            <RotateCcw className="h-3 w-3" />
                            Réessayer
                          </button>
                        )}
                      </div>
                    </div>
                  );

                case 'rate_limit_note':
                  return (
                    <p key={item.id} className="text-[13px] italic text-muted-foreground">
                      {item.message}
                    </p>
                  );

                case 'user_stopped':
                  // Arrêt volontaire : note discrète grise, jamais le bandeau amber
                  // d'`agent_error`. L'avocat sait qu'il a interrompu, pas une erreur.
                  return (
                    <p key={item.id} className="text-[13px] italic text-muted-foreground">
                      Vous avez arrêté la conversation.
                    </p>
                  );

                case 'server_finishing':
                  // Tour encore en cours hors de portée de ce socket (autre instance backend,
                  // ou fenêtre de replay dépassée) : même registre discret que `user_stopped`.
                  // Le texte complet arrive par rechargement de l'historique à `turn_settled`.
                  // Libellé revu le 2026-09-29 (retour user, ex-PR #1798) : dire à l'avocat CE QUI
                  // s'est passé (il a perdu la connexion, pas Hector) et ce qu'il doit faire (rien).
                  // `open_elsewhere` (2026-10-09) : le tour est tenu par une autre socket vivante,
                  // personne n'a été déconnecté. Dire « déconnecté » à un avocat qui vient
                  // d'ouvrir un second onglet est faux et l'envoie chercher une panne réseau.
                  return (
                    <p key={item.id} className="text-[13px] italic text-muted-foreground">
                      {item.cause === 'open_elsewhere'
                        ? "Hector répond déjà à cette conversation dans un autre onglet ou sur un autre appareil. La réponse s'affichera ici quand il aura terminé."
                        : "Vous avez été déconnecté de la conversation. Hector continue à travailler ; la réponse s'affichera quand il aura terminé."}
                    </p>
                  );

                default:
                  return null;
              }
            })}

            {/* Boutons de navigation de l'agent — TOUJOURS sous le texte final
                (cf. `uiActionsItem`). Interactifs sur le dernier échange seulement :
                `onUiAction` est `undefined` ailleurs → `AgentUiActionsCard` rend
                `null` (pas de boutons cliquables sur un vieux message). */}
            {renderedUiActions.length > 0 && (
              <AgentUiActionsCard actions={renderedUiActions} onAction={onUiAction} />
            )}

            {/* Pied de message à HAUTEUR FIXE (min-h = h-6 des boutons), slot UNIQUE
                partagé entre la plume (pendant l'écriture) et les boutons d'action (tour
                terminé) — jamais les deux à la fois (showTypingIndicator ⟺ isStreaming,
                showActionButtons ⟺ !isStreaming). Les loger au MÊME emplacement garantit :
                (1) une distance contenu↔plume CONSTANTE — un seul écart `space-y-1` au-dessus,
                identique quel que soit le type du dernier item (texte, tools, réflexion) ;
                (2) aucun saut au passage plume→boutons (même slot, même hauteur). On ne réserve
                ce slot que s'il a quelque chose à montrer (pas de vide sous un message
                historique sans actions). Boutons : toujours visibles sur le dernier message
                (isLast), au survol pour les précédents. */}
            {(showTypingIndicator || showActionButtons) && (
              <div className="flex min-h-[24px] items-center">
                {showTypingIndicator ? (
                  <TypingIndicator isActive delayMs={200} startedAt={turnStartedAt} />
                ) : (
                  <MessageActionButtons
                    messageContent={fullContent}
                    messageId={feedbackMessageId}
                    feedbackInitial={feedbackInitial}
                    onSaveToResearch={() => setSaveDialogOpen(true)}
                    className={isLast ? undefined : "opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto"}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showActionButtons && (
        <SaveToResearchDialog
          open={saveDialogOpen}
          onOpenChange={setSaveDialogOpen}
          message={{ id: 'streaming', role: 'assistant', content: fullContent } as ChatMessage}
          timeline={timeline}
          caseId={caseId}
          preselectedCaseId={caseId}
        />
      )}
    </div>
  );
});

StreamingTimeline.displayName = 'StreamingTimeline';
